import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, Loader2, Plus, Trash2 } from "lucide-react";

const localWhen = (iso) => new Date(iso).toLocaleString([], {
  weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
});

export function HuddleSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/huddle").then(({ data }) => setCfg(data.config)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!cfg) return <Card title="Monthly Student Huddle"><div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cc-blue" /></div></Card>;

  const setSlot = (i, patch) => {
    const slots = cfg.slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setCfg({ ...cfg, slots });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled, topic: cfg.topic, duration_minutes: Number(cfg.duration_minutes) || 30,
        timezone: cfg.timezone, invite_days: cfg.invite_days, skip_months: cfg.skip_months,
        slots: cfg.slots.map((s) => ({
          label: s.label,
          disciplines: typeof s.disciplines === "string" ? s.disciplines.split(",").map((d) => d.trim()).filter(Boolean) : s.disciplines,
          time: s.time, teams_url: s.teams_url,
        })),
      };
      await api.put("/huddle", payload);
      toast.success("Huddle settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const skipNext = () => {
    const first = cfg.slots?.[0]?.next?.[0];
    if (!first) return;
    const key = first.key;
    const list = cfg.skip_months || [];
    setCfg({ ...cfg, skip_months: list.includes(key) ? list.filter((k) => k !== key) : [...list, key] });
  };
  const nextKey = cfg.slots?.[0]?.next?.[0]?.key;
  const skipping = (cfg.skip_months || []).includes(nextKey);

  return (
    <Card title="Monthly Student Huddle" data-testid="huddle-settings">
      <div className="space-y-4">
        <p className="text-sm text-zinc-500">
          Every student whose rotation covers the second Tuesday of the month sees their discipline's session on their
          dashboard, and is emailed a calendar invite 7 days and 1 day before. Paste each session's standing Teams link once.
        </p>
        <div className="flex items-center gap-2.5">
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="huddle-enabled" />
          <span className="text-sm text-zinc-700">{cfg.enabled ? "Running" : "Paused — nothing shows or sends"}</span>
        </div>

        <div className="space-y-3" data-testid="huddle-slots">
          {(cfg.slots || []).map((s, i) => (
            <div key={i} className="border rounded-lg p-3 space-y-2.5" data-testid={`huddle-slot-${i}`}>
              <div className="flex flex-wrap gap-2 items-end">
                <div className="w-28">
                  <label className="text-xs text-zinc-400">Session</label>
                  <Input value={s.label || ""} onChange={(e) => setSlot(i, { label: e.target.value })} data-testid={`huddle-label-${i}`} />
                </div>
                <div className="w-28">
                  <label className="text-xs text-zinc-400">Start time</label>
                  <Input type="time" value={s.time || ""} onChange={(e) => setSlot(i, { time: e.target.value })} data-testid={`huddle-time-${i}`} />
                </div>
                <div className="flex-1 min-w-[10rem]">
                  <label className="text-xs text-zinc-400">Disciplines (comma separated)</label>
                  <Input value={Array.isArray(s.disciplines) ? s.disciplines.join(", ") : s.disciplines || ""}
                    onChange={(e) => setSlot(i, { disciplines: e.target.value })} data-testid={`huddle-disc-${i}`} />
                </div>
                <button onClick={() => setCfg({ ...cfg, slots: cfg.slots.filter((_, idx) => idx !== i) })}
                  className="text-zinc-400 hover:text-red-600 pb-2" data-testid={`huddle-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div>
                <label className="text-xs text-zinc-400">Standing Teams meeting link</label>
                <Input value={s.teams_url || ""} onChange={(e) => setSlot(i, { teams_url: e.target.value })}
                  placeholder="https://teams.microsoft.com/l/meetup-join/…" data-testid={`huddle-url-${i}`} />
              </div>
              {(s.next || []).length > 0 && (
                <p className="text-xs text-zinc-500" data-testid={`huddle-next-${i}`}>
                  Next: {s.next.map((o) => localWhen(o.start)).join("  ·  ")}
                </p>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setCfg({ ...cfg, slots: [...(cfg.slots || []), { label: "", disciplines: [], time: "13:00", teams_url: "" }] })}
            data-testid="huddle-add-slot"><Plus className="w-4 h-4 mr-1" />Add a session</Button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Session length (minutes)">
            <Input type="number" value={cfg.duration_minutes ?? 30} onChange={(e) => setCfg({ ...cfg, duration_minutes: e.target.value })} data-testid="huddle-duration" />
          </Field>
          <Field label="Times above are in">
            <Input value={cfg.timezone || ""} onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })} data-testid="huddle-timezone" />
            <p className="text-xs text-zinc-400 mt-1">An IANA name like America/New_York. Students always see it in their own timezone.</p>
          </Field>
        </div>
        <Field label="What you'll cover (shown on the dashboard and in the invite)">
          <Textarea rows={2} value={cfg.topic || ""} onChange={(e) => setCfg({ ...cfg, topic: e.target.value })} data-testid="huddle-topic" />
        </Field>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="huddle-save">
            <Users className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save huddle settings"}
          </Button>
          {nextKey && (
            <Button variant="outline" size="sm" onClick={skipNext} data-testid="huddle-skip">
              {skipping ? `Un-skip ${nextKey}` : `Skip ${nextKey}`}
            </Button>
          )}
          {(cfg.skip_months || []).length > 0 && (
            <span className="text-xs text-amber-600">Skipping: {(cfg.skip_months || []).join(", ")} (remember to save)</span>
          )}
        </div>
      </div>
    </Card>
  );
}
